// Where a menu opened beside something sits (Menu's placement "beside": the board's tech menu, opened
// from a technician's name). Pure sums over the screen's numbers, so a test can hold them:
// - beside it, on its end side (to the right of a name), a small gap away; on the start side only when
//   the end side has no room, and over it only when neither side has (a phone);
// - level with its top, moved up only as far as it must to end inside the screen;
// - never over the page's banner (the site header) when it's in view: it stops under it, unless the
//   thing it opened from is itself higher;
// - taller than the room left, it stops at the bottom edge and scrolls inside (maxHeight).

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface BesideInput {
  /** What it opened from (a name's box), in the viewport's px; a point is a box of no size. */
  anchor: Box;
  /** The menu's own size, before anything limits its height. */
  menu: { width: number; height: number };
  viewport: { width: number; height: number };
  /** Where the page's banner ends (its bottom edge, 0 with none in view): the menu stays under it. */
  keepBelow?: number;
  /** Between it and what it opened from. */
  gap: number;
  /** Kept clear at the screen's edges. */
  edge: number;
}

export interface BesidePlace {
  left: number;
  top: number;
  /** As tall as it may be; less than the menu's height means it scrolls inside. */
  maxHeight: number;
  /** end: after it (to the right); start: before it; over: neither side had room. */
  side: 'end' | 'start' | 'over';
}

export function placeBeside({ anchor, menu, viewport, keepBelow = 0, gap, edge }: BesideInput): BesidePlace {
  const endLeft = anchor.left + anchor.width + gap;
  const startLeft = anchor.left - gap - menu.width;
  const rightmost = Math.max(edge, viewport.width - edge - menu.width);
  const side: BesidePlace['side'] =
    endLeft + menu.width <= viewport.width - edge ? 'end' : startLeft >= edge ? 'start' : 'over';
  const left = side === 'end' ? endLeft : side === 'start' ? startLeft : Math.min(endLeft, rightmost);

  // the highest it may start: under the banner, or the name's own top if the name is higher still
  const ceiling = Math.max(edge, Math.min(keepBelow > 0 ? keepBelow + edge : edge, anchor.top));
  const floor = viewport.height - edge;
  const top = Math.max(ceiling, Math.min(anchor.top, floor - menu.height));
  return { left, top, maxHeight: Math.max(0, floor - top), side };
}
